import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import {
  usePasswordResetComplete,
  usePasswordResetVerifyOtp,
} from "@orbit/api-client-react";
import { useAuth } from "../hooks/useAuth";
import { AuthLayout } from "../components/AuthLayout";
import {
  clearResetSession,
  readResetSession,
  type ResetSessionState,
} from "./ForgotPassword";

export function ForgotPasswordVerifyPage() {
  const [, navigate] = useLocation();
  const { signIn } = useAuth();
  const [session, setSession] = useState<ResetSessionState | null>(null);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const verifyMut = usePasswordResetVerifyOtp();
  const completeMut = usePasswordResetComplete();

  useEffect(() => {
    const s = readResetSession();
    if (!s) {
      // No active reset attempt in this tab — bounce to the start.
      navigate("/forgot-password");
      return;
    }
    setSession(s);
  }, [navigate]);

  if (!session) return null;

  // 12-char minimum mirrors lib/api-zod CustomerPasswordSchema. Keep
  // this literal in sync with PublicSignupForm — drift here would let
  // a "valid" 11-char password through the client and 422 server-side.
  const passwordOk = newPassword.length >= 12;
  const codeOk = /^\d{6}$/.test(code);

  const onVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!codeOk) {
      setError("Enter the 6-digit code we texted you.");
      return;
    }
    try {
      await verifyMut.mutateAsync({
        resetToken: session.resetToken,
        code,
      });
      setOtpVerified(true);
    } catch (err) {
      const e = err as {
        status?: number;
        apiError?: { code?: string; message?: string; details?: unknown };
      };
      if (e.status === 401 && e.apiError?.code === "INVALID_OTP") {
        const remaining =
          (e.apiError.details as { attemptsRemaining?: number } | undefined)
            ?.attemptsRemaining ?? null;
        setError(
          remaining != null
            ? `Wrong code. ${remaining} attempt${remaining === 1 ? "" : "s"} left.`
            : "Wrong code. Try again.",
        );
      } else if (e.status === 410) {
        setError(
          "This code is no longer valid. Start over to get a new one.",
        );
        clearResetSession();
      } else if (e.status === 404) {
        // ATTEMPT_NOT_FOUND — uniform-response decoy from /request, or
        // the attempt expired and was swept. Same UX either way.
        setError("Couldn't find that reset request. Start over.");
        clearResetSession();
      } else {
        setError(e.apiError?.message ?? "Couldn't verify the code.");
      }
    }
  };

  const onComplete = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!passwordOk) {
      setError("Password must be at least 12 characters.");
      return;
    }
    try {
      const r = await completeMut.mutateAsync({
        resetToken: session.resetToken,
        newPassword,
      });
      clearResetSession();
      signIn(r.customerId, r.tokens);
      navigate("/cards");
    } catch (err) {
      const e = err as {
        status?: number;
        apiError?: { code?: string; message?: string };
      };
      if (e.status === 412) {
        setError("Code wasn't verified. Try the code step again.");
        setOtpVerified(false);
      } else if (e.status === 410) {
        setError("Reset session expired. Start over.");
        clearResetSession();
      } else {
        setError(e.apiError?.message ?? "Couldn't set your new password.");
      }
    }
  };

  // Phase 32 (D104) — channel-aware hint. The session was persisted by
  // ForgotPassword and includes both `channel` ('phone'|'email') and
  // `targetMasked`, so we can render "We texted ****567" vs
  // "We emailed m***a@example.com" without a server round-trip.
  const sentHint =
    session.channel === "email"
      ? `We emailed ${session.targetMasked}.`
      : `We texted ${session.targetMasked || session.phoneMasked || "your number"}.`;

  return (
    <AuthLayout
      title={otpVerified ? "Set a new password" : "Enter the code"}
      subtitle={
        otpVerified
          ? "Pick something you'll remember."
          : sentHint
      }
    >
      {!otpVerified ? (
        <form onSubmit={onVerify} className="flex flex-col gap-4">
          <div>
            <label htmlFor="code" className="label">6-digit code</label>
            <input
              id="code"
              className="input tracking-[0.5em] text-center text-lg"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="••••••"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
              data-testid="input-reset-otp"
            />
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <button
            type="submit"
            className="btn-primary w-full"
            disabled={!codeOk || verifyMut.isPending}
            data-testid="button-verify-reset-code"
          >
            {verifyMut.isPending ? "Verifying…" : "Verify code"}
          </button>
          <p className="text-center text-sm text-ink-dark">
            Didn't get it?{" "}
            <a href="/forgot-password" className="underline text-navy">
              Start over
            </a>
          </p>
        </form>
      ) : (
        <form onSubmit={onComplete} className="flex flex-col gap-4">
          <div>
            <label htmlFor="newPassword" className="label">New password</label>
            <div className="relative">
              <input
                id="newPassword"
                className="input pr-16"
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                minLength={12}
                required
                data-testid="input-new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-2 my-auto text-xs font-medium text-navy underline"
                tabIndex={-1}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            <p className="mt-1 text-xs text-ink-dark/70">
              At least 12 characters.
            </p>
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <button
            type="submit"
            className="btn-primary w-full"
            disabled={!passwordOk || completeMut.isPending}
            data-testid="button-complete-reset"
          >
            {completeMut.isPending ? "Saving…" : "Save & sign in"}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
